import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import {
  formatSettlementCurrency,
  getBusinessPayoutDestination,
  sumPaymentBatch,
  type BusinessPayoutDestination,
  type PlatformPaymentSettlement,
} from "@/lib/platform-settlements";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessName: string;
  business?: BusinessPayoutDestination | null;
  payments: PlatformPaymentSettlement[];
  channel: string;
  onChannelChange: (v: string) => void;
  reference: string;
  onReferenceChange: (v: string) => void;
  notes: string;
  onNotesChange: (v: string) => void;
  busy: boolean;
  onConfirm: () => void;
};

export function RecordPayoutDialog({
  open,
  onOpenChange,
  businessName,
  business,
  payments,
  channel,
  onChannelChange,
  reference,
  onReferenceChange,
  notes,
  onNotesChange,
  busy,
  onConfirm,
}: Props) {
  const totals = sumPaymentBatch(payments);
  const destination = getBusinessPayoutDestination(business);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send payout to {businessName}</DialogTitle>
          <DialogDescription>
            Disburse {formatSettlementCurrency(totals.net, totals.currency)} using the details below, then record the transfer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div
            className={`rounded-lg border p-3 text-sm space-y-2 ${
              destination.connected && destination.accountDetail
                ? "border-border bg-muted/50"
                : "border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30"
            }`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Send to</p>
              <Badge variant="secondary">{destination.methodLabel}</Badge>
            </div>
            {destination.connected && destination.accountDetail ? (
              <>
                {destination.providerLabel ? (
                  <p className="font-medium text-foreground">{destination.providerLabel}</p>
                ) : null}
                <p className="font-mono text-lg font-semibold text-foreground">{destination.accountDetail}</p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No MoMo or bank details on file. Contact the business before sending funds.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Transfer type</Label>
            <Select value={channel} onValueChange={onChannelChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank">Bank transfer</SelectItem>
                <SelectItem value="momo">Mobile money</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Transfer reference</Label>
            <Input
              value={reference}
              onChange={(e) => onReferenceChange(e.target.value)}
              placeholder={channel === "bank" ? "Bank reference / transaction ID" : "MoMo transaction ID"}
            />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => onNotesChange(e.target.value)} placeholder="Optional" />
          </div>

          <div className="rounded-lg border bg-muted/50 p-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Service total</span>
              <span>{formatSettlementCurrency(totals.gross, totals.currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Buukd fees (kept)</span>
              <span>{formatSettlementCurrency(totals.platformFee, totals.currency)}</span>
            </div>
            <div className="flex justify-between border-t pt-2 font-semibold">
              <span>Pay business</span>
              <span>{formatSettlementCurrency(totals.net, totals.currency)}</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Recording…
              </>
            ) : (
              "Record payout"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
